/**
 * <flagship-pill-nav> — header controller
 * ----------------------------------------------------------------
 * Responsibilities:
 *   1. Smart sticky: hide on scroll-down past threshold, show on up.
 *   2. Pill rail: capture wheel/trackpad to scroll horizontally;
 *      circular arrow buttons advance ~200px smoothly; arrows dim
 *      at the edges.
 *   3. Dropdown overlays: open on hover (desktop) / focus / click;
 *      width and left set on each open to match the visible rail
 *      rect; mouse-leave-shell closes them; Esc closes them.
 *
 * No GSAP, no plugins. Boot block lives at the END of this IIFE so
 * all prototype methods are populated before any instance is
 * created (per tasks/lessons.md Rule 16).
 */
(function () {
  'use strict';

  function attach(header) {
    if (header._fpnController) header._fpnController.destroy();
    header._fpnController = new PillNavController(header);
  }

  // ---------------------------------------------------------------

  function PillNavController(header) {
    this.header = header;
    this.rail = header.querySelector('[data-rail]');
    this.railShell = header.querySelector('[data-rail-shell]');
    this.arrowLeft = header.querySelector('[data-rail-arrow="left"]');
    this.arrowRight = header.querySelector('[data-rail-arrow="right"]');
    this.pills = Array.from(header.querySelectorAll('[data-pill]'));

    // Dropdown overlay lives OUTSIDE the header now — pulled out so its
    // backdrop-filter samples the page underneath instead of being trapped
    // in the header's transform stacking context. Look it up at document
    // level, matched to this header via data-for-section.
    this.dropdownHost = document.querySelector(
      '[data-dropdown-host][data-for-section="' + header.id + '"]'
    );
    // The bar's glass is a childless sibling of the header (so pill-hover
    // repaints can't dirty its backdrop). It doesn't slide with the header
    // automatically — mirror data-hidden onto it in _setHidden.
    this.glass = document.querySelector(
      '[data-nav-glass][data-for-section="' + header.id + '"]'
    );
    this.dropdowns = this.dropdownHost
      ? Array.from(this.dropdownHost.querySelectorAll('[data-dropdown-for]'))
      : [];

    this.hideThreshold = parseFloat(header.dataset.hideThreshold || '100');
    this.stickyMode    = header.dataset.stickyMode || 'always_show';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._lastScrollY = window.scrollY || 0;
    this._rafScrollPending = false;
    this._hidden = false;
    this._openDropdownId = null;
    this._closeTimer = null;

    // Bind handlers — keeps `this` correct AND lets us removeEventListener.
    this._onWindowScroll = this._onWindowScroll.bind(this);
    this._tickWindowScroll = this._tickWindowScroll.bind(this);
    this._onRailWheel = this._onRailWheel.bind(this);
    this._onRailScroll = this._onRailScroll.bind(this);
    this._tickRailEdges = this._tickRailEdges.bind(this);
    this._onArrowLeft = this._onArrowLeft.bind(this);
    this._onArrowRight = this._onArrowRight.bind(this);
    this._onPillEnter = this._onPillEnter.bind(this);
    this._onPillClick = this._onPillClick.bind(this);
    this._onPillFocus = this._onPillFocus.bind(this);
    this._onPlainPillFocus = this._onPlainPillFocus.bind(this);
    this._onShellLeave = this._onShellLeave.bind(this);
    this._onShellEnter = this._onShellEnter.bind(this);
    this._onDocKey = this._onDocKey.bind(this);
    this._onDocPointer = this._onDocPointer.bind(this);
    this._onResize = this._onResize.bind(this);

    // Always bind the scroll listener — even when the header is in
    // always_show mode, we still need it to drive the mobile-only
    // top-row hide-on-scroll-down behavior. The full-header hide is
    // gated inside the tick by stickyMode.
    if (!this.reducedMotion) {
      window.addEventListener('scroll', this._onWindowScroll, { passive: true });
    }
    window.addEventListener('resize', this._onResize);

    if (this.rail) {
      // wheel non-passive so we can preventDefault and convert deltaY → scrollLeft
      this.rail.addEventListener('wheel', this._onRailWheel, { passive: false });
      this.rail.addEventListener('scroll', this._onRailScroll, { passive: true });
    }
    if (this.arrowLeft) this.arrowLeft.addEventListener('click', this._onArrowLeft);
    if (this.arrowRight) this.arrowRight.addEventListener('click', this._onArrowRight);

    this.pills.forEach((pill) => {
      if (pill.dataset.hasDropdown === 'true') {
        pill.addEventListener('mouseenter', this._onPillEnter);
        pill.addEventListener('focus', this._onPillFocus);
        pill.addEventListener('click', this._onPillClick);
      } else {
        // Pills with NO dropdown of their own still have to dismiss one
        // that's already open. Hovering from Shop onto Store Locator
        // never left the <header>, so header.mouseleave didn't fire and
        // the Shop menu just sat there under a pill the user had clearly
        // moved off of. Reuse the same deferred close as the shell so a
        // cursor cutting diagonally across a neighbouring pill on its way
        // down into the dropdown still gets its 150ms grace — the host's
        // mouseenter cancels the timer the moment it arrives.
        pill.addEventListener('mouseenter', this._onShellLeave);
        // Keyboard has no diagonal path, so tabbing onto a plain pill
        // closes immediately.
        pill.addEventListener('focus', this._onPlainPillFocus);
      }
    });
    if (this.header) {
      this.header.addEventListener('mouseleave', this._onShellLeave);
      this.header.addEventListener('mouseenter', this._onShellEnter);
    }
    // Dropdown-host lives outside the <header> now, so mouse moves from a
    // pill into the dropdown panel cross a DOM-subtree boundary and fire
    // header.mouseleave. Attach the same handlers to the host so its
    // mouseenter cancels the pending close.
    if (this.dropdownHost) {
      this.dropdownHost.addEventListener('mouseleave', this._onShellLeave);
      this.dropdownHost.addEventListener('mouseenter', this._onShellEnter);
    }
    // ...but the HOST is pointer-events:none — it's a full-width positioning
    // shell and must not eat clicks on the page behind it. An element with
    // pointer-events:none never fires mouseenter/mouseleave, so the two
    // listeners above never ran, the 150ms grace timer was never cancelled,
    // and the dropdown closed 150ms after the cursor left the pill EVEN WHILE
    // hovering inside the panel. It only appeared to work when a click landed
    // inside that window, which is why it felt intermittent.
    //
    // The panels themselves are pointer-events:auto, so bind there.
    this.dropdowns.forEach((dd) => {
      dd.addEventListener('mouseenter', this._onShellEnter);
      dd.addEventListener('mouseleave', this._onShellLeave);
    });
    document.addEventListener('keydown', this._onDocKey);
    // Outside-tap closes any open dropdown. Without this, tapping off
    // the dropdown on a touch device leaves the pill stuck in its
    // selected state (no mouseleave on touch). 'pointerdown' fires
    // before 'click' so the dropdown closes before any tap-target
    // outside the nav consumes the event.
    document.addEventListener('pointerdown', this._onDocPointer);

    // Initial sync
    this._tickRailEdges();
    // Position the rail SYNCHRONOUSLY (no rAF) and INSTANT (no smooth
    // scroll) so the current pill appears in view from the very first
    // paint — no visible "reset to 0 then animate" flash. The defer
    // attribute on the script means this runs after the DOM is parsed
    // but typically before first paint, so the user never sees the
    // rail at scrollLeft=0.
    this._scrollCurrentIntoView();
  }

  // ---------- Smart sticky ----------

  PillNavController.prototype._onWindowScroll = function () {
    if (this._rafScrollPending) return;
    this._rafScrollPending = true;
    requestAnimationFrame(this._tickWindowScroll);
  };

  PillNavController.prototype._tickWindowScroll = function () {
    this._rafScrollPending = false;
    const y = window.scrollY || 0;
    const dy = y - this._lastScrollY;
    if (Math.abs(dy) < 2) return; // micro-scroll filter

    // Don't toggle while a dropdown is open (would yank the menu away).
    if (this._openDropdownId !== null) {
      this._lastScrollY = y;
      return;
    }

    // Full-header smart-hide only when explicitly enabled.
    if (this.stickyMode === 'smart_hide') {
      if (dy > 0 && y > this.hideThreshold) this._setHidden(true);
      else if (dy < 0) this._setHidden(false);
    }

    this._lastScrollY = y;
  };

  PillNavController.prototype._setHidden = function (hidden) {
    if (hidden === this._hidden) return;
    this._hidden = hidden;
    if (hidden) {
      this.header.setAttribute('data-hidden', 'true');
      if (this.glass) this.glass.setAttribute('data-hidden', 'true');
      // Dropdown-host is outside the header now, so it won't slide up with
      // the hide animation. Close any open dropdown so it doesn't sit
      // orphaned in the viewport while the rail is gone.
      if (this._openDropdownId !== null) this._closeDropdown();
    } else {
      this.header.removeAttribute('data-hidden');
      if (this.glass) this.glass.removeAttribute('data-hidden');
    }
  };

  // ---------- Pill rail scroll mechanics ----------

  PillNavController.prototype._onRailWheel = function (e) {
    if (!this.rail) return;
    // If user is scrolling primarily vertically, redirect to horizontal.
    // We use Math.abs(deltaY) > Math.abs(deltaX) to detect vertical intent;
    // some mice/touchpads already produce deltaX directly which we leave alone.
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      // Only intercept if there's room to scroll horizontally — otherwise
      // let the page scroll normally so we don't trap the user at the rail.
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      if (max <= 1) return;
      e.preventDefault();
      this.rail.scrollLeft += e.deltaY;
    }
  };

  PillNavController.prototype._onRailScroll = function () {
    // If the user is scrolling the pill rail with a dropdown open, the
    // pill the dropdown belongs to is moving but the dropdown is anchored
    // to where the pill USED to be — looks broken. Close on rail scroll.
    if (this._openDropdownId !== null) this._closeDropdown();
    if (this._rafEdgesPending) return;
    this._rafEdgesPending = true;
    requestAnimationFrame(this._tickRailEdges);
  };

  PillNavController.prototype._tickRailEdges = function () {
    this._rafEdgesPending = false;
    if (!this.rail) return;
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const x = this.rail.scrollLeft;

    // All pills fit — hide arrows entirely.
    // Write an explicit 'false' (not removeAttribute): the section's
    // critical CSS hides arrows whenever data-fits ISN'T 'false', so the
    // attribute's absence = "JS hasn't measured yet" (arrows stay hidden,
    // no pop-in on load) and 'false' = the JS opt-in that shows them.
    if (this.railShell) {
      if (max <= 1) this.railShell.setAttribute('data-fits', 'true');
      else this.railShell.setAttribute('data-fits', 'false');
    }

    if (this.arrowLeft) {
      if (x <= 1) this.arrowLeft.setAttribute('data-edge', 'true');
      else this.arrowLeft.removeAttribute('data-edge');
    }
    if (this.arrowRight) {
      if (x >= max - 1) this.arrowRight.setAttribute('data-edge', 'true');
      else this.arrowRight.removeAttribute('data-edge');
    }

    // Edge fade masks: only show the left fade once the user has scrolled right
    // (so the first pill like HOME isn't obscured at the start). Mirror on right.
    // Fade ramps from 0px to 24px over the first 30px of scroll on each side.
    const fadeMax = 24;
    const ramp = 30;
    const leftFade = Math.min(x / ramp, 1) * fadeMax;
    const rightFade = max <= 1 ? 0 : Math.min((max - x) / ramp, 1) * fadeMax;
    this.rail.style.setProperty('--rail-fade-left', leftFade.toFixed(1) + 'px');
    this.rail.style.setProperty('--rail-fade-right', rightFade.toFixed(1) + 'px');
  };

  // Bring the current-page pill into view ONLY when it isn't already
  // visible. If it is already inside the rail's viewport (with a small
  // edge margin), do nothing — no point animating a pill that's right
  // where the user can already see it. When out of view, scroll to
  // center it; clamps to the rail's valid scroll range so pills near
  // either edge just dock against that edge.
  PillNavController.prototype._scrollCurrentIntoView = function () {
    if (!this.rail) return;
    const current = this.rail.querySelector('.flagship-pill-nav__pill[aria-current="page"]');
    if (!current) return;
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    if (max <= 0) return;                   // Rail fits all pills — nothing to scroll.

    const railRect = this.rail.getBoundingClientRect();
    const pillRect = current.getBoundingClientRect();
    // Pill's left/right relative to the rail's current viewport.
    const pillLeftInView  = pillRect.left  - railRect.left;
    const pillRightInView = pillRect.right - railRect.left;
    // Margin: pill needs to be at least this far from the rail's edges
    // to count as "comfortably visible." If only the leading sliver of
    // a pill is showing, we DO want to scroll it into view.
    const margin = 12;
    const alreadyVisible =
      pillLeftInView  >= margin &&
      pillRightInView <= this.rail.clientWidth - margin;
    if (alreadyVisible) return;

    // Out of view (or only partly visible) — position the rail so the
    // pill is centered. MUST be instant: the rail's CSS sets
    // scroll-behavior: smooth (for the arrow buttons), and a direct
    // scrollLeft assignment OBEYS that — so without the override below,
    // this "initial positioning" smooth-scrolled the rail from 0 to the
    // current pill on every page load (a long left-to-right slide for
    // later pills). Temporarily force auto so the rail just appears at
    // its final position, then restore so arrows stay smooth.
    const pillOffsetInRail = pillLeftInView + this.rail.scrollLeft;
    const targetLeft = pillOffsetInRail - (this.rail.clientWidth / 2) + (pillRect.width / 2);
    const clamped = Math.max(0, Math.min(max, targetLeft));
    this.rail.style.scrollBehavior = 'auto';
    this.rail.scrollLeft = clamped;
    this.rail.style.scrollBehavior = '';
  };

  PillNavController.prototype._onArrowLeft = function () {
    if (!this.rail) return;
    this.rail.scrollBy({ left: -200, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  };
  PillNavController.prototype._onArrowRight = function () {
    if (!this.rail) return;
    this.rail.scrollBy({ left: 200, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  };

  PillNavController.prototype._onResize = function () {
    // Rail edges may change when viewport changes; recompute.
    this._tickRailEdges();
    if (this._openDropdownId !== null) this._positionOpenDropdown();
  };

  // ---------- Dropdowns ----------

  PillNavController.prototype._onPillEnter = function (e) {
    const id = e.currentTarget.dataset.pill;
    this._openDropdown(id, e.currentTarget);
  };
  PillNavController.prototype._onPillFocus = function (e) {
    const id = e.currentTarget.dataset.pill;
    this._openDropdown(id, e.currentTarget);
  };
  PillNavController.prototype._onPillClick = function (e) {
    // Pills with a dropdown attached NEVER navigate on click — they
    // only toggle the dropdown. Reason: touch devices have no hover,
    // so clicking the pill is the only way to reveal the sub-menu.
    // If clicking also navigated, the user couldn't browse the
    // children at all. Sub-navigation happens via the column links
    // INSIDE the dropdown, the column-title link, or the "View all"
    // bar at the top of the panel — which is the route to this pill's
    // own page, and the reason the pill itself doesn't need one.
    e.preventDefault();
    const id = e.currentTarget.dataset.pill;
    if (this._openDropdownId === id) {
      this._closeDropdown();
    } else {
      this._openDropdown(id, e.currentTarget);
    }
  };

  PillNavController.prototype._openDropdown = function (id, pillEl) {
    // Cancel any pending close-timer from a previous mouseleave —
    // the user is back in the bar.
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    }
    if (this._openDropdownId === id) return;
    this._closeDropdown();

    const dropdown = this.dropdowns.find((d) => d.dataset.dropdownFor === id);
    if (!dropdown) return;

    // _closeDropdown() just scheduled a 220ms timer to add `hidden` to the
    // panel we are leaving. Clearing that timer without doing its job left the
    // old panel un-hidden: no data-open so invisible, but still laid out and
    // still pointer-events:auto, so it sat over the page swallowing hovers.
    // That is why moving between menus intermittently killed hover until a
    // reload. Hide every other panel outright instead — switching menus wants
    // an instant swap anyway; the exit animation only matters when closing to
    // nothing, which still goes through _closeDropdown's timer.
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    this.dropdowns.forEach((d) => {
      if (d !== dropdown) {
        d.removeAttribute('data-open');
        d.setAttribute('hidden', '');
      }
    });

    this._openDropdownId = id;
    dropdown.removeAttribute('hidden');
    this._positionDropdown(dropdown);
    void dropdown.offsetWidth; // force reflow so the transition runs
    requestAnimationFrame(() => {
      dropdown.setAttribute('data-open', 'true');
    });

    if (pillEl) pillEl.setAttribute('aria-expanded', 'true');
  };

  PillNavController.prototype._closeDropdown = function () {
    const id = this._openDropdownId;
    this._openDropdownId = null;
    if (id == null) return;

    const dropdown = this.dropdowns.find((d) => d.dataset.dropdownFor === id);
    if (dropdown) {
      dropdown.removeAttribute('data-open');
      this._closeTimer = setTimeout(() => {
        if (this._openDropdownId !== id && dropdown) {
          dropdown.setAttribute('hidden', '');
        }
      }, 220);
    }
    this.pills.forEach((p) => {
      if (p.dataset.pill === id) p.setAttribute('aria-expanded', 'false');
    });
  };

  PillNavController.prototype._positionDropdown = function (dropdown) {
    if (!this.railShell || !this.header || !dropdown) return;
    const shellRect = this.railShell.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();
    // dropdown-host is now position:fixed at top:0 (outside the <header>),
    // so the dropdown's absolute coords are viewport-relative. Match the
    // dropdown's box to the rail's visible width/left, and anchor its top
    // to the header's actual bottom (not the rail-shell's) so it clears
    // any padding/shadow below the rail. Small 4px gap.
    dropdown.style.width = shellRect.width + 'px';
    dropdown.style.left = shellRect.left + 'px';
    dropdown.style.top = (headerRect.bottom + 4) + 'px';
  };

  PillNavController.prototype._positionOpenDropdown = function () {
    const id = this._openDropdownId;
    if (id == null) return;
    const dropdown = this.dropdowns.find((d) => d.dataset.dropdownFor === id);
    if (dropdown) this._positionDropdown(dropdown);
  };

  PillNavController.prototype._onShellLeave = function () {
    // Don't close immediately — the 8px gap between the bar and the
    // dropdown panel is technically *outside* the header element's
    // subtree (the dropdown sits in an absolute-positioned host below).
    // A cursor moving diagonally from a pill toward the dropdown
    // briefly passes through that gap, which fires mouseleave on the
    // header. Without a delay, this kills the dropdown before the
    // user reaches it. 150ms is roughly the natural cursor-traversal
    // window — short enough that intentional leaves still feel
    // responsive, long enough that a deliberate move down stays alive.
    if (this._leaveTimer) clearTimeout(this._leaveTimer);
    this._leaveTimer = setTimeout(() => {
      this._closeDropdown();
      this._leaveTimer = null;
    }, 150);
  };

  PillNavController.prototype._onPlainPillFocus = function () {
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    }
    if (this._openDropdownId !== null) this._closeDropdown();
  };

  PillNavController.prototype._onShellEnter = function () {
    // Cursor came back into the header subtree (either the bar or
    // the dropdown panel — both are descendants of the <header>).
    // Cancel any pending close.
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    }
  };

  PillNavController.prototype._onDocKey = function (e) {
    if (e.key === 'Escape' && this._openDropdownId !== null) {
      this._closeDropdown();
    }
  };

  // Close the dropdown when the user pointers down OUTSIDE the header
  // AND outside the dropdown-host (now a sibling of the header). If the
  // event is inside either subtree — tapping a pill, scrolling inside
  // the rail, clicking a dropdown link — leave the dropdown alone.
  PillNavController.prototype._onDocPointer = function (e) {
    if (this._openDropdownId === null) return;
    if (!this.header) return;
    if (this.header.contains(e.target)) return;
    if (this.dropdownHost && this.dropdownHost.contains(e.target)) return;
    this._closeDropdown();
  };

  // ---------- Cleanup ----------

  PillNavController.prototype.destroy = function () {
    window.removeEventListener('scroll', this._onWindowScroll);
    window.removeEventListener('resize', this._onResize);
    if (this.rail) {
      this.rail.removeEventListener('wheel', this._onRailWheel);
      this.rail.removeEventListener('scroll', this._onRailScroll);
    }
    if (this.arrowLeft) this.arrowLeft.removeEventListener('click', this._onArrowLeft);
    if (this.arrowRight) this.arrowRight.removeEventListener('click', this._onArrowRight);
    this.pills.forEach((p) => {
      p.removeEventListener('mouseenter', this._onPillEnter);
      p.removeEventListener('focus', this._onPillFocus);
      p.removeEventListener('click', this._onPillClick);
      p.removeEventListener('mouseenter', this._onShellLeave);
      p.removeEventListener('focus', this._onPlainPillFocus);
    });
    if (this.header) {
      this.header.removeEventListener('mouseleave', this._onShellLeave);
      this.header.removeEventListener('mouseenter', this._onShellEnter);
    }
    if (this.dropdownHost) {
      this.dropdownHost.removeEventListener('mouseleave', this._onShellLeave);
      this.dropdownHost.removeEventListener('mouseenter', this._onShellEnter);
    }
    document.removeEventListener('keydown', this._onDocKey);
    document.removeEventListener('pointerdown', this._onDocPointer);
    if (this._closeTimer) clearTimeout(this._closeTimer);
    if (this._leaveTimer) clearTimeout(this._leaveTimer);
    this.header.removeAttribute('data-hidden');
    if (this.glass) this.glass.removeAttribute('data-hidden');
    this.dropdowns.forEach((d) => {
      d.removeAttribute('data-open');
      d.setAttribute('hidden', '');
    });
  };

  // ---------------------------------------------------------------
  // Header-height publisher
  // ---------------------------------------------------------------
  // Dawn's inline script in layout/theme.liquid tries to set
  // --header-group-height by querying for <header-component>, which
  // doesn't exist when this flagship pill-nav has replaced Dawn's
  // header. Other sections (flagship-hero-reveal) use that variable to
  // pull themselves flush under the sticky header — without it, they
  // fall back to 0 and don't pull up. So we publish the value ourselves.
  function publishHeaderHeight() {
    const headers = document.querySelectorAll('.flagship-pill-nav');
    let total = 0;
    headers.forEach((h) => { total += h.offsetHeight || 0; });
    if (total <= 0) return;
    document.body.style.setProperty('--header-group-height', total + 'px');
    document.body.style.setProperty('--header-height', total + 'px');
  }

  // Re-publish on resize (font/layout shifts can change header height)
  // and after fonts load. ResizeObserver catches anything else (e.g. the
  // sticky-mode toggle, dropdown opens that change the row height).
  function bindHeaderHeightWatchers() {
    publishHeaderHeight();
    window.addEventListener('resize', publishHeaderHeight, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(publishHeaderHeight).catch(() => {});
    }
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(publishHeaderHeight);
      document.querySelectorAll('.flagship-pill-nav').forEach((h) => ro.observe(h));
    }
  }

  // ---------------------------------------------------------------
  // Boot — at the END of the IIFE so all prototype assignments above
  // are populated before any instance is created. See lessons.md Rule 16.
  // ---------------------------------------------------------------
  // ---------------------------------------------------------------
  // Cart drawer trigger
  //
  // The cart icons stay real <a href="/cart"> links, so the cart is still
  // reachable with JS off, with the drawer script blocked, or before
  // cart-drawer.js has defined the element. The click is only intercepted
  // once the component actually exists AND its open() is available —
  // otherwise the default navigation is left alone rather than swallowed,
  // which is what would strand someone on a dead icon.
  //
  // Delegated on document because the drawer is rendered by the same section
  // and Shopify re-renders that subtree in the theme editor.
  // ---------------------------------------------------------------
  function onCartTriggerClick(e) {
    // Let modified clicks do their normal thing (new tab, download, etc).
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const trigger = e.target.closest('[data-cart-drawer-trigger]');
    if (!trigger) return;

    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || typeof drawer.open !== 'function') return; // fall through to /cart

    e.preventDefault();
    drawer.open();
  }

  function init() {
    document.querySelectorAll('.flagship-pill-nav').forEach(attach);
    bindHeaderHeightWatchers();
    document.removeEventListener('click', onCartTriggerClick);
    document.addEventListener('click', onCartTriggerClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const header = e.target && e.target.querySelector
      ? e.target.querySelector('.flagship-pill-nav')
      : null;
    if (header) attach(header);
  });
})();
